import { FC, useMemo, useEffect } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import { BaseChartTemplate } from './BaseChartTemplate';

interface NumberOfLatePaymentsChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<{
    openModal?: () => void;
    customMessageOverlay?: React.ReactNode;
  }>;
  monthsToShow?: number;
  // BUG-UI-131 FIX: Accept authoritative late payments count from member data
  // member.number_of_late_payments from lender_member_summary view is more accurate
  // than stats[].number_of_late_payments from financial_health_stats_v2 (which may be stale)
  memberLatePayments?: number | null;
}

export const NumberOfLatePaymentsChart: FC<NumberOfLatePaymentsChartProps> = ({
  data,
  showDebug,
  onExpandClick,
  monthsToShow = 12,
  memberLatePayments
}) => {
  // Format number for Y-axis
  const formatNumber = (value: number) => value.toString();

  // Format tooltip values
  const formatTooltip = (value: number) => [`${value}`, 'Late Payments'];

  // BUG-UI-131 FIX: Use memberLatePayments prop if available (authoritative source)
  // Otherwise fall back to stats data
  const authoritativeLatePayments = memberLatePayments ?? null;

  // Check if there are any late payments in the last 24 months
  // BUG-UI-131 FIX: If memberLatePayments is provided and > 0, don't show "Perfect Payment Record!"
  const hasNoLatePaymentsIn24Months = useMemo(() => {
    // If we have authoritative member data showing late payments, use that
    if (authoritativeLatePayments != null && authoritativeLatePayments > 0) {
      return false; // Has late payments - don't show celebration
    }

    // Sort data chronologically
    const sortedData = [...data].sort((a, b) =>
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );

    // Get the last 24 months of data (or all if less than 24 months)
    const last24Months = sortedData.slice(-24);

    // Check if all months have zero late payments
    return last24Months.every(month => (month.number_of_late_payments || 0) === 0);
  }, [data, authoritativeLatePayments]);
  
  // Message to display when there are no late payments
  const celebrationMessage = useMemo(() => {
    if (!hasNoLatePaymentsIn24Months) return null;
    
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-white/90 rounded-lg">
        <div className="text-4xl mb-3">🎉</div>
        <h3 className="text-xl font-bold text-emerald-600 mb-2">Perfect Payment Record!</h3>
        <p className="text-sm text-slate-600 max-w-xs">
          The member has maintained an excellent payment record with no late payments in the last 24 months.
        </p>
      </div>
    );
  }, [hasNoLatePaymentsIn24Months]);
  
  // Render the overlay with current data
  // BUG-UI-131 FIX: Use authoritative memberLatePayments if available
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    // Use authoritative member data if available, otherwise fall back to stats
    const statsCount = visibleData[visibleData.length - 1]?.number_of_late_payments || 0;
    const currentCount = authoritativeLatePayments ?? statsCount;
    const hasLatePayments = currentCount > 0;

    return (
      <div 
        className="absolute left-16 top-2 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg border border-slate-200 shadow-sm"
        style={{ zIndex: 1 }}
      >
        <div className="grid gap-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">Late Payments</div>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-brand-blue">
                {currentCount}
              </div>
              <div
                className={`text-sm font-medium px-2 py-1 rounded-full ${
                  hasLatePayments ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                }`}
              >
                {hasLatePayments ? 'Has Late Payments' : 'All Payments On Time'}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
            <span className={hasLatePayments ? 'text-amber-400' : 'text-emerald-400'}>
              {hasLatePayments ? '⚠️' : '✨'}
            </span>
            <span className="text-sm">
              {hasLatePayments 
                ? 'Late payments may affect credit score' 
                : 'Clean payment streak maintained'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // useEffect to pass the celebrationMessage to the modal through the onExpandClick reference
  useEffect(() => {
    if (onExpandClick && onExpandClick.current) {
      // Attach the message to the ref so it can be accessed by ChartExpandModal
      onExpandClick.current.customMessageOverlay = hasNoLatePaymentsIn24Months ? celebrationMessage : null;
    }
  }, [onExpandClick, hasNoLatePaymentsIn24Months, celebrationMessage]);

  return (
    <BaseChartTemplate
      data={data}
      showDebug={showDebug}
      monthsToShow={monthsToShow}
      onExpandClick={onExpandClick}
      title="Late Payments"
      dataKey="number_of_late_payments"
      color="#F59E0B" // amber-500
      yAxisFormatter={formatNumber}
      tooltipFormatter={formatTooltip}
      renderOverlay={renderOverlay}
      min={0}
      max={10}
      isCurrentFinances={true}
      customMessageOverlay={celebrationMessage}
    />
  );
};
